// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import qs from 'query-string';
import React, { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalFooter } from '@heroui/react';
import { Button } from '@/components/ui/button';
import { type ModalProps } from '@/stores/use-modal-store.ts';
import { toast } from 'sonner';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import { useTranslation } from 'react-i18next';

export const DeleteMessageModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const { t } = useTranslation();
  const { apiUrl, query } = data;
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const onClick = async () => {
    try {
      setIsLoading(true);

      const url = qs.stringifyUrl({
        url: apiUrl || '',
        query: {
          ...query,
          userId: user?.id,
        },
      });

      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      onClose();
    } catch (error) {
      // TODO: translate
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="text-center text-2xl font-bold">
                {t('DeleteMessageModal.title')}
              </div>
              <div className="text-center">
                {t('DeleteMessageModal.description')} <br />
                {t('DeleteMessageModal.permanentDelete')}
              </div>
            </ModalHeader>
            <ModalFooter>
              <Button disabled={isLoading} onClick={onClose} variant="ghost">
                {t('DeleteMessageModal.cancel')}
              </Button>
              <Button disabled={isLoading} color="danger" onClick={onClick}>
                {t('DeleteMessageModal.confirm')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
